import {
  fetchResortAccommodations,
  fetchResortRelated,
  fetchResortShortInfo,
  fetchResortShortWeatherReport,
} from 'api/resort/static';
import { IResortPageInfo, IResortPathInfo } from 'api/resort/types';
import { useOffers } from 'api/resort/useOffers';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { AxiosResponse } from 'axios';
import { ErrorBlock } from 'components/common/ErrorBlock';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { LdJsonBreadCrumbsResort } from 'components/LdJson';
import { Accommodation } from 'components/resort/Accomodation';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';
import { ITALodgingReq } from 'components/common/PlanYourTrip/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { SortAccommodations } from 'components/common/SortAccomodations';
import {
  SortByAccommodations,
  SortByAccommodationsDefault,
} from 'components/common/SortAccomodations/constants';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React, { useCallback, useMemo, useState } from 'react';
import { Routes } from 'routes';
import { getResortSlug } from 'util/memcache';
import { sortAccommodationsByPrice, accommodationSortingBySortType } from 'util/accommodationsSortings';
import { fetchResortMeta } from 'api/region/static';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import styles from './lodging.module.scss';

export type ResortLodgingProps = {
  resort: IResortPageInfo,
  pathInfo: IResortPathInfo,
  accommodations: IAccommodationPage | null,
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-lodging-page';
export const TEMPLATE_ID = '3.2.4';
export const TEMPLATE_NAME = 'Resort Lodging';

export default function ResortLodging({
  resort, pathInfo, accommodations, templateName,
}: ResortLodgingProps) {
  const { t } = useOTSTranslation('common');
  const [formData, setFormData] = useState<ITALodgingReq>({
    checkIn: '',
    checkOut: '',
    numRooms: 1,
    numAdults: 2,
  });
  const [sortBy, setSortBy] = useState<SortByAccommodations>(SortByAccommodationsDefault);

  const locationList = useMemo(
    () => (accommodations?.data ?? []).map((a) => a.location_id).sort(),
    [accommodations],
  );
  const { offerMap, areOffersLoading, error } = useOffers(
    locationList,
    formData.checkIn,
    formData.checkOut,
    formData.numAdults,
    formData.numAdults,
  );

  const sorted = useMemo(() => {
    if (accommodationSortingBySortType[sortBy]) {
      return sortAccommodationsByPrice(accommodations?.data ?? [], offerMap, sortBy);
    }
    return (accommodations?.data ?? []);
  }, [(accommodations?.data ?? []), sortBy, offerMap]);

  const handleChangeAccommodationParams = useCallback(
    (params: ITALodgingReq) => setFormData(params),
    [setFormData],
  );

  return (
    <>
      <div className="w-100">
        <PlanYourTrip
          resort={pathInfo}
          hasSkiRental={pathInfo.hasSkiRental}
          hasFlights={pathInfo.hasFlights}
          hasCarRental={pathInfo.hasCarRental}
          onLodgingChange={handleChangeAccommodationParams}
          rentalUrl={resort.rentalUrl}
          liftsUrl={resort.liftsUrl}
          lessonsUrl={resort.lessonsUrl}
          isPartner={resort.isPartner}
          resortCountry={resort?.region?.country_code}
          templateName={templateName}
          isCondensed
        />

        <>
          {error && <ErrorBlock className={styles.error}>{error}</ErrorBlock>}
          <SortAccommodations
            setSortBy={setSortBy}
            sortBy={sortBy}
            className={styles.header}
            title={resort.title}
          />
          {locationList.length === 0 && (
            <h2 className="m-4 p-4" style={{ textAlign: 'center' }}>
              <span>{t('resort.lodging.noResults')}</span>
            </h2>
          )}
          <div className={styles.offerList}>
            {sorted.map((acc) => (
              <Accommodation
                className={styles.accommodation}
                key={acc.location_id}
                accommodation={acc}
                isFresh
                offers={offerMap[acc.location_id]}
                areOffersLoading={areOffersLoading}
                dataClickId={`resort-${resort.uuid}-${acc.location_id}`}
                dataClickCategory="TripAdvisor"
              />
            ))}
          </div>
        </>

      </div>
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={resort.region}
        fn={Routes.resortTripPlanningLodging}
      />
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortLodgingProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const fetchedResort = await fetchResortShortInfo(uuid);

  return {
    type: 'resort',
    route: 'lodging',
    pathInfo: cache.data,
    accommodations: await fetchResortAccommodations(uuid),
    resort: fetchedResort,
    shortWeather: await fetchResortShortWeatherReport(uuid),

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.LODGING,
      tab: CurrentTab.LODGING,
      head: HeaderNavCurLink.TRIP_PLANNING,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort lodging',
    hero: {
      title: 'resort.lodging.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.lodging.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.lodging.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
  };
};

// This function gets called at build time
export const getResortLodgingStaticProps = wrapStaticProps(fetchProps);
export const getResortLodgingServerSideProps = wrapServerSideProps(fetchProps);
